// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { fromJS } from 'immutable';
import omit from 'lodash/omit';

import { Cell, Grid, findMatch } from 'react-flexr';

import {
  Stepper,
  Step,
  StepLabel,
  StepButton,
  StepContent,
  Paper,
  Typography,
  FormControlLabel
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';

import green from '@mui/material/colors/green';
import orange from '@mui/material/colors/orange';
import red from '@mui/material/colors/red';

import Button from 'app/common/button';

import InputAdornment from 'app/common/form/Adornment';
import AutoCompleteDAWA from 'app/common/autocomplete-dawa';

import InputImages from './images';

import Form, {
  DatePicker,
  RangeSlider,
  SelectField,
  WithComment
} from 'app/common/form';

import Input from 'app/common/form/input';
import InputRentCalc from 'app/common/form/inputRentCalculator/InputRentCalc';

import Checkbox from 'app/common/form/checkbox';

import Alert from 'app/common/alert';

import i18n from 'app/common/i18n';
import {
  createDwelling,
  CREATE_DWELLING_FAIL,
  upload,
  updateDwelling,
  UPDATE_DWELLING_FAIL,
  singleDwelling,
  clear
} from '../redux/modules/organizations';
import { setTitle } from 'app/layout/redux';
import { traverse, getInitialDwelling } from './utils';

import StepPreview from './StepPreview';

import { toastr } from 'app/common/toastr';
import ToolTipIcon from '../common/tooltipIcon';
import AlertWithLink from '../common/alertWithLink';
// import { goal } from '../../lib/tracking/clicky';

type Props = {
  user: null | { get: (arg: any) => any }
};

type State = {
  saving: boolean,
  modified: boolean,

  readonly: boolean,
  data: {},
  fields: {},

  feedback: null,
  errors: {},
  files: {},
  stepIndex: number,
  stepIndexMax: number,

  nowDate: Date,

  matchMedia: {
    palm: string,
    desktop: string
  }
};

class Update extends React.Component<Props, State> {
  i18n: (string) => string;

  static fetchData({ dispatch, params }) {
    // TODO: Could singleDwelling trigger clear if uuid is not present?
    return dispatch(params.uuid ? singleDwelling(params.uuid) : clear());
  }

  constructor(props: Props) {
    super(props);

    const uuid = props.params && props.params.uuid;
    const dwelling =
      uuid && props.dwelling && props.dwelling.uuid === uuid
        ? props.dwelling
        : null;

    const data = getInitialDwelling(dwelling, props.user).toJS();

    if ((this._isAdmin(props) || this._isUserVA(props)) && !dwelling) {
      data.fromFacebook = true;
    }

    const readonly = data.locked && !this._isAdmin(props);

    if (!['PLUS', 'PREMIUM'].includes(data.listingType)) {
      data.chatable = true;
      data.phoneable = false;
      data.emailable = false;
    }

    this.state = {
      admin: this._isAdmin(props),
      facebookAccess: this._isAdmin(props) || this._isUserVA(props),
      saving: false,
      modified: false,
      readonly,
      data,
      fields: {},
      nowDate: new Date(),
      feedback: null,
      errors: {},
      files: fromJS((dwelling && dwelling.images) || []),
      stepIndex: readonly ? this.steps.length - 1 : 0,
      stepIndexMax: 0,
      matchMedia: {
        palm: findMatch('palm'),
        desktop: findMatch('desktop')
      }
    };
  }

  resetCreateState() {
    const data = getInitialDwelling(null, this.props.user).toJS();
  
    if (this._isAdmin() || this._isUserVA()) {
      data.fromFacebook = true;
    }
  
    if (!['PLUS', 'PREMIUM'].includes(data.listingType)) {
      data.chatable = true;
      data.phoneable = false;
      data.emailable = false;
    }
  
    this.setState({
      readonly: false,
      data,
      files: fromJS([]),
      modified: false,
      errors: {},
      feedback: null,
      stepIndex: 0,
      stepIndexMax: 0
    });
  }

  componentDidMount() {
    const { params = {}, location } = this.props;
  
    this.props.setTitle(
      params.uuid
        ? this.props.i18n('Update dwelling')
        : this.props.i18n('Create dwelling')
    );
  
    if (params.uuid) {
      if (!this.props.dwelling || this.props.dwelling.uuid !== params.uuid) {
        this.props.singleDwelling(params.uuid);
      }
  
      return;
    }
  
    this.props.clear();
  
    if (location && location.pathname === '/dwellings/create') {
      this.resetCreateState();
    }
  }

  componentDidUpdate(prevProps) {
    const prevUuid = prevProps.params && prevProps.params.uuid;
    const uuid = this.props.params && this.props.params.uuid;
  
    const prevPath = prevProps.location && prevProps.location.pathname;
    const path = this.props.location && this.props.location.pathname;
  
    const prevLocationKey = prevProps.location && prevProps.location.key;
    const locationKey = this.props.location && this.props.location.key;
  
    const isCreatePath = path === '/dwellings/create';
    const enteredCreatePath = prevPath !== path && isCreatePath;
    const repeatedCreateNavigation =
      isCreatePath && prevLocationKey !== locationKey;
  
    if (enteredCreatePath || repeatedCreateNavigation) {
      this.props.clear();
      this.props.setTitle(this.props.i18n('Create dwelling'));
      this.resetCreateState();
      return;
    }
  
    if (prevUuid !== uuid) {
      this.props.setTitle(
        uuid
          ? this.props.i18n('Update dwelling')
          : this.props.i18n('Create dwelling')
      );
  
      if (uuid) {
        if (!this.props.dwelling || this.props.dwelling.uuid !== uuid) {
          this.props.singleDwelling(uuid);
        }
  
        return;
      }
  
      this.props.clear();
      this.resetCreateState();
      return;
    }
  
    if (
      uuid &&
      this.props.dwelling &&
      this.props.dwelling.uuid === uuid &&
      this.props.dwelling !== prevProps.dwelling
    ) {
      const data = getInitialDwelling(
        this.props.dwelling,
        this.props.user
      ).toJS();
  
      data.uuid = this.props.dwelling.uuid;
  
      const readonly = data.locked && !this._isAdmin();
  
      if (!['PLUS', 'PREMIUM'].includes(data.listingType)) {
        data.chatable = true;
        data.phoneable = false;
        data.emailable = false;
      }
  
      const isSameDraftFlow =
        this.state.data &&
        this.state.data.uuid &&
        this.state.data.uuid === uuid;
  
      this.setState({
        readonly,
        data,
        files: fromJS(this.props.dwelling.images || []),
        modified: false,
        errors: {},
        feedback: null,
        stepIndex: isSameDraftFlow
          ? this.state.stepIndex
          : readonly
          ? this.steps.length - 1
          : 0,
        stepIndexMax: isSameDraftFlow ? this.state.stepIndexMax : 0
      });
    }
  }

  componentWillUnmount() {
    this.props.setTitle();
  }

  _isAdmin(props = this.props) {
    return (props.user && props.user.get('role') === 'admin') || false;
  }

  _isUserVA(props = this.props) {
    return (props.user && props.user.get('type') === 'VA') || false;
  }

  _isUserBusiness(props = this.props) {
    return (props.user && props.user.get('type') === 'BUSINESS') || false;
  }

  adminPrompt(stepIndex) {
    const isConfirmed = window.confirm('Do you want to save the changes?');
    if (!isConfirmed) {
      const stepIndexMax = Math.max(stepIndex, this.state.stepIndexMax);
      const initialData = getInitialDwelling(
        this.props.dwelling,
        this.props.user
      ).toJS();
      this.setState({
        stepIndex,
        stepIndexMax,
        modified: false,
        data: initialData
      });
      return false;
    }
    return true;
  }

  async showStep(stepIndex) {
    if (stepIndex >= this.state.stepIndex) {
      if (!this.validate()) {
        return;
      }
    }
  
    if (this.state.readonly) {
      return;
    }
  
    if (this.state.saving) {
      return;
    }
  
    if (this.state.modified && this._isAdmin()) {
      const savedChanges = this.adminPrompt(stepIndex);
      if (!savedChanges) {
        return;
      }
    }
  
    try {
      let action = null;
  
      if (this.state.modified) {
        action = await this.submit();
  
        if (
          action &&
          [UPDATE_DWELLING_FAIL, CREATE_DWELLING_FAIL].includes(action.type)
        ) {
          return;
        }
      }
  
      const stepIndexMax = Math.max(stepIndex, this.state.stepIndexMax);
  
      const createdUuid =
        action &&
        action.createdUuid;
  
      this.setState(
        {
          stepIndex,
          stepIndexMax,
          modified: false,
          errors: {}
        },
        () => {
          if (
            createdUuid &&
            (!this.props.params || this.props.params.uuid !== createdUuid)
          ) {
            this.props.navigate(`/dwellings/${createdUuid}`, {
              replace: true
            });
          }
        }
      );
    } catch (err) {
      if (__DEV__) {
        console.error(err);
      }
    }
  }

  handleNext = () => {
    this.setState({ feedback: null });
    const { stepIndex } = this.state;
    if (stepIndex < this.steps.length) {
      this.showStep(stepIndex + 1);
    }
  };

  handlePrev = () => {
    this.setState({ feedback: null });
    const { stepIndex } = this.state;
    if (stepIndex > 0) {
      this.showStep(stepIndex - 1);
    }
  };

  renderStepActions(stepIndex) {
    const finalStep = stepIndex === this.steps.length - 1;

    const navigationDisabled = this.state.saving;

    const labelNext = this.state.saving
      ? this.props.i18n('Saving')
      : finalStep
      ? this.state.readonly || this._isAdmin()
        ? this.props.i18n('Close')
        : ['PENDING_REVIEW', 'APPROVED'].includes(this.state.data.status)
        ? this.props.i18n('Save')
        : this.props.i18n('Create listing')
      : this.props.i18n('Next');
    return (
      <Grid style={{ margin: '18px 0 0' }}>
        <Cell size="1/2" style={{ textAlign: 'left', padding: 0 }}>
          {stepIndex > 0 && (
            <Button
              flat
              disabled={navigationDisabled}
              label={this.props.i18n('Back')}
              onClick={this.handlePrev}
            />
          )}
        </Cell>
        <Cell size="1/2" style={{ textAlign: 'right', padding: 0 }}>
          <Button
            data-cy="nextButton"
            primary
            disabled={/*step.isComplete() === false  ||*/ navigationDisabled}
            label={labelNext}
            onClick={finalStep ? this.handleSubmit : this.handleNext}
          />
        </Cell>
      </Grid>
    );
  }

  handleSubmit = async (ev) => {
    ev.preventDefault();
    const { data } = this.state;
    const final = this.state.stepIndex === this.steps.length - 1;

    if (final && !this.validate() && !this._isAdmin()) {
      return;
    }

    if (final) {
      if (this.state.readonly || this._isAdmin()) {
        return this.props.navigate('/dwellings/');
      }
      this.setValue('status', 'PENDING_REVIEW');
    }
    const action = await this.submit();

    if (final) {
      const uuid =
        action &&
        action.payload &&
        action.payload.result &&
        action.payload.result.uuid;

      toastr.success(
        this.props.i18n('Listing has been created'),
        this.props.i18n('Your listing is now pending review')
      );

      if (uuid) {
        this.props.navigate(`/dwellings/${uuid}/status?from=createDwelling`);
      }
    }
  };

  serializeData(data, files) {
    const { terms, ...rest } = data;
    return {
      ...rest,
      terms: terms.map((term) => {
        if (term.key === 'PETS') {
          return {
            ...term,
            value:
              term.value === '_TRUE'
                ? true
                : term.value === '_FALSE'
                ? false
                : null
          };
        }
        return term;
      }),
      images: files.map((file) => file.get('filename')).filter(Boolean),
      payment: undefined
    };
  }

  submit = async () => {
    if (
      this.state.readonly ||
      (this.state.data.status === 'APPROVED' && !this._isAdmin())
    ) {
      return {};
    }
  
    this.setState({ feedback: null, saving: true });
  
    try {
      const { data } = this.state;
  
      const routeUuid = this.props.params && this.props.params.uuid;
      const stateUuid = data && data.uuid;
      const uuid = routeUuid || stateUuid;
  
      const isUpdate = Boolean(uuid);
  
      let action;
  
      if (isUpdate) {
        action = await this.props.updateDwelling(
          uuid,
          this.serializeData(data, this.state.files)
        );
      } else {
        action = await this.props.createDwelling(
          this.props.params.id,
          this.serializeData(data, this.state.files)
        );
  
        const createdUuid =
          action &&
          action.payload &&
          action.payload.result &&
          action.payload.result.uuid;
  
        if (createdUuid) {
          data.uuid = createdUuid;
          this.setState({ data });
  
          action.createdUuid = createdUuid;
        }
      }
  
      if (
        action.type === (isUpdate ? UPDATE_DWELLING_FAIL : CREATE_DWELLING_FAIL)
      ) {
        const { error: { response } = {} } = action || {};
  
        if (response && response.errors) {
          this.setState({
            errors: response.errors,
            feedback: {
              type: 'danger',
              response: this.props.i18n(
                'Some fields were not properly filled out!'
              )
            }
          });
          return action;
        }
  
        this.setState({
          feedback: {
            type: 'danger',
            response:
              response && response.message
                ? this.props.i18n(response.message)
                : this.props.i18n(
                    'Oops! Something went wrong. Please try again or contact customer support'
                  )
          }
        });
  
        return action;
      }
  
      return action;
    } finally {
      this.setState({ saving: false });
    }
  };

  onImageChange = (files) => {
    this.setState({ files, modified: true });
  };

  getValue(name) {
    const path = name.split('.');
    return path.reduce((state, key) => state[key], this.state.data);
  }

  setValue(name, value) {
    const { data } = this.state;
    const path = name.split('.');
    traverse(data, path, value);
    this.setState({ data, modified: true });
  }

  handleChange = (ev) => {
    const name = ev.target.name;
    if (ev.target.type === 'checkbox') {
      this.onChangeError(ev, this.setValue(name, ev.target.checked));
    } else {
      this.onChangeError(ev, this.setValue(name, ev.target.value));
    }
  };

  setError(name, value) {
    const { errors } = this.state;
    const path = name.split('.');
    traverse(errors, path, value);
    this.setState({ errors });
  }

  removeError = (name) => {
    const { errors = {} } = this.state || {};
    const newErrors = omit(errors, name.split('.')[0]);
    this.setState({ errors: newErrors });
  };

  onChangeError = (ev, fn) => {
    const value = ev.target.value;
    const field = ev.target.name;
    switch (field) {
      case 'size.area':
        {
          if (value < 1) {
            this.setError(field, 'Boligareal er ikke udfyldt korrekt');
          } else {
            this.removeError(field);
          }
        }
        break;
      case 'size.rooms':
        {
          if (value < 1) {
            this.setError(field, 'Antal værelser er ikke udfyldt korrekt');
          } else if (value.includes(',') || value.includes('.')) {
            this.setError(field, 'Angiv kun antallet af hele værelser');
          } else {
            this.removeError(field);
          }
        }
        break;
      case 'economy.rent':
        {
          if (value < 1) {
            this.setError(field, 'Husleje er ikke udfyldt korrekt');
          } else {
            this.removeError(field);
          }
        }
        break;
      default:
        this.removeError(field);
        break;
    }
    () => fn;
  };

  handleAnonymous = (value) => {
    const { anonymous = [] } = this.state.data;
    if (anonymous && anonymous.includes(value)) {
      const idx = anonymous.indexOf(value);
      anonymous.splice(idx, 1);
      this.setValue('anonymous', anonymous);
      return;
    }
    this.setValue('anonymous', [...anonymous, value]);
  };

  handleChangeMerge = (ev) => {
    if (/\[\]$/.test(ev.target.name)) {
      const name = ev.target.name.replace(/\[\]$/, '');
      const value = ev.target.value;
      const values = this.getValue(name);
      let idx = null;
      for (var i = 0; i < values.length; i++) {
        if (values[i].key === value.key) {
          idx = i;
          break;
        }
      }
      if (idx !== null) {
        if (value.value === '') {
          values.splice(idx, 1);
        } else {
          values.splice(idx, 1, value);
        }
      } else if (typeof value.value !== 'undefined') {
        values.push(value);
      }

      this.setValue(name, values);
    } else {
      const name = ev.target.name;
      const value = {
        ...this.getValue(name),
        ...ev.target.value
      };
      this.setValue(name, value);
    }
  };

  renderInput({ label, ...rest }) {
    const props = {
      label: this.props.i18n(label),
      onChange: this.handleChange,
      ...rest
    };
    return <Input {...props} />;
  }

  validate(stepIndex = this.state.stepIndex) {
    const { errors } = this.state;
    this.steps[stepIndex].validate();
    if (
      this._isUserVA() &&
      errors.consents &&
      Object.keys(errors).length === 1
    ) {
      return true;
    }
    const isValid = Object.keys(errors).length > 0 ? false : true;
    return isValid;
  }

  steps = [
    {
      label: 'The lease',
      validate: () => {
        const { data } = this.state;
        const { location = {}, size = {}, available, timeframe = {} } = data;
        const missingField = 'Du mangler at udfylde feltet';

        if (data.fromFacebook) {
          if (!size.rooms && !size.area) {
            this.setError('size.rooms', missingField);
            this.setError('size.area', missingField);
          }
        } else {
          if (!size.rooms) {
            this.setError('size.rooms', missingField);
          }
          if (!size.area) {
            this.setError('size.area', missingField);
          }
        }
        if (!location.id) {
          if (!data.fromFacebook) {
            this.setError(
              'location',
              'Du skal vælge en bestemt adresse fra listen. Hvis din adresse ikke var der, så kontakt os.'
            );
          } else {
            if (!location.zip) {
              this.setError('location.zip', missingField);
            } else if (!/[0-9]{4}/.test(location.zip)) {
              this.setError('location.zip', 'Zip skal være tal på 4 cifre');
            }

            if (!location.city) {
              this.setError('location.city', missingField);
            }
          }
        }

        if (timeframe.type === 'DATE') {
          if (!timeframe.date) {
            this.setError('timeframe.date', missingField);
          } else if (available && available > timeframe.date) {
            this.setError(
              'timeframe.date',
              'Slutdato for lejeperioden skal være efter overtagelsesdato.'
            );
          }
        }
      },
      isComplete: () => {
        const { data } = this.state;
        const { location = {}, size = {}, available, timeframe = {} } = data;
        if (timeframe.type === 'DATE') {
          if (!timeframe.date || (available && available > timeframe.date)) {
            return false;
          }
        }
        const validLocation = data.fromFacebook
          ? location.address && location.zip && location.city
          : location.id;

        const validSize = data.fromFacebook
          ? size.rooms > 0 || size.area > 0
          : size.rooms > 0 && size.area > 0;
        return Boolean(validLocation && validSize);
      },
      handleLocationSelect: (choosen, unit) => {
        const { location = {} } = this.state.data;
        if (typeof choosen === 'string' && choosen === location.address) {
          return;
        }

        this.setValue('location', unit || {});
        this.removeError('location');
        //TODO: can be delete @daniel?
        if (!unit) {
          this.setState({
            fields: {
              ...this.state.fields,
              location: {
                errorText: this.props.i18n(
                  "You need to pick a specific address. If your address wasn't in the list, please contact us."
                )
              }
            }
          });
          return;
        }
        this.setState({
          fields: {
            ...this.state.fields,
            location: {
              underlineStyle: {
                borderWidth: '2px',
                borderColor: green[500]
              }
            }
          }
        });
      },
      content: () => {
        const { errors, data, fields, facebookAccess, admin } = this.state;
        const {
          location = {},
          anonymous,
          size: { rooms, area },
          status
        } = data;
        const field = fields.location || {};
        // TODO: Avoid during this on each render!
        const now = new Date();
        now.setDate(1);
        now.setHours(0, 0, 0, 0);
        const yearAhead = new Date();
        yearAhead.setDate(yearAhead.getDate() + 365);
        const isNonProfit = this.state.data.terms.find(
          (term) => term.key === 'NONPROFIT' && term.value === true
        )
          ? true
          : false;
        return this.renderWithPaper(
          <div>
            {facebookAccess ? (
              <Checkbox
                checked={data.fromFacebook}
                label="From facebook"
                name="fromFacebook"
                onChange={this.handleChange}
              />
            ) : null}
            {admin ? (
              <Checkbox
                checked={data.customAddress}
                label="Custom address"
                name="customAddress"
                onChange={this.handleChange}
              />
            ) : null}
            {data.fromFacebook || data.customAddress ? (
              <div>
                <Input
                  label={this.props.i18n('Address')}
                  placeholder="fx Marielundvej 20, 2730 Herlev"
                  defaultValue={location.address}
                  name="location.address"
                  type="string"
                  error={errors.location}
                  required
                  onChange={this.handleChange}
                  onBlur={(event) => {
                    if (!location.id && !facebookAccess) {
                      this.setError(
                        'location',
                        'Du skal vælge en bestemt adresse fra listen. Hvis din adresse ikke var der, så kontakt os.'
                      );
                    }
                    if (facebookAccess) {
                      const address = event.target && event.target.value;
                      this.setState({
                        data: {
                          ...data,
                          location: Object.assign(data.location, { address })
                        }
                      });
                    }
                  }}
                  onFocus={() =>
                    this.setState({ fields: { ...fields, location: null } })
                  }
                  fullWidth
                />
                <Input
                  label={this.props.i18n('Zip')}
                  placeholder="fx 2730"
                  defaultValue={location.zip}
                  name="location.zip"
                  type="string"
                  error={errors.location}
                  required
                  onChange={this.handleChange}
                  onBlur={(event) => {
                    if (!location.id && !facebookAccess) {
                      this.setError(
                        'location',
                        'Du skal vælge en bestemt adresse fra listen. Hvis din adresse ikke var der, så kontakt os.'
                      );
                    }
                    if (facebookAccess) {
                      const zip = event.target && event.target.value;
                      this.setState({
                        data: {
                          ...data,
                          location: Object.assign(data.location, { zip })
                        }
                      });
                    }
                  }}
                  onFocus={() =>
                    this.setState({ fields: { ...fields, location: null } })
                  }
                  fullWidth
                />
                <Input
                  label={this.props.i18n('City')}
                  placeholder="fx Herlev"
                  defaultValue={location.city}
                  name="location.city"
                  type="string"
                  error={errors.location}
                  required
                  onChange={this.handleChange}
                  onBlur={(event) => {
                    if (!location.id && !facebookAccess) {
                      this.setError(
                        'location',
                        'Du skal vælge en bestemt adresse fra listen. Hvis din adresse ikke var der, så kontakt os.'
                      );
                    }
                    if (facebookAccess) {
                      const city = event.target && event.target.value;
                      this.setState({
                        data: {
                          ...data,
                          location: Object.assign(data.location, { city })
                        }
                      });
                    }
                  }}
                  onFocus={() =>
                    this.setState({ fields: { ...fields, location: null } })
                  }
                  fullWidth
                />
              </div>
            ) : (
              <AutoCompleteDAWA
                locked={
                  !data.uuid || this._isAdmin() ? false : status !== 'DRAFT'
                }
                label={this.props.i18n('Address')}
                placeholder="fx Marielundvej 20, 2730 Herlev"
                defaultValue={location.address}
                anonymous={anonymous && anonymous.includes('LOCATION')}
                onAnonymousSelect={this.handleAnonymous}
                onSelect={this.steps[0].handleLocationSelect}
                error={errors.location}
                onBlur={() => {
                  if (!location.id) {
                    this.setError(
                      'location',
                      'Du skal vælge en bestemt adresse fra listen. Hvis din adresse ikke var der, så kontakt os.'
                    );
                  }
                }}
                onFocus={() =>
                  this.setState({ fields: { ...fields, location: null } })
                }
                {...field}
              />
            )}

            <Grid>
              <Cell size="1/2">
                <Input
                  label={this.props.i18n('Rooms')}
                  defaultValue={rooms}
                  name="size.rooms"
                  type="number"
                  pattern="[0-9]+"
                  error={errors.size && errors.size.rooms}
                  inputProps={{
                    min: 1,
                    max: 10,
                    step: 1
                  }}
                  required
                  onChange={this.handleChange}
                  fullWidth
                />
              </Cell>

              <Cell size="1/2">
                <Input
                  label={this.props.i18n('Size (in m²)')}
                  defaultValue={area}
                  name="size.area"
                  type="number"
                  error={errors.size && errors.size.area}
                  pattern="[0-9]+"
                  inputProps={{
                    min: 5,
                    max: 1000
                  }}
                  required
                  onChange={this.handleChange}
                  fullWidth
                />
              </Cell>

              <Cell size="1/2">
                <SelectField
                  label={this.props.i18n('Dwelling type')}
                  options={[
                    { value: 'ROOM', label: this.props.i18n('Room') },
                    {
                      value: 'APARTMENT',
                      label: this.props.i18n('Apartment')
                    },
                    {
                      value: 'VILLA_APARTMENT',
                      label: this.props.i18n('Villa apartment')
                    },
                    { value: 'HOUSE', label: this.props.i18n('House') },
                    {
                      value: 'TOWNHOUSE',
                      label: this.props.i18n('Town house')
                    },
                    {
                      value: 'FARMHOUSE',
                      label: this.props.i18n('Farm house')
                    }
                  ]}
                  defaultValue={data.type.dwelling}
                  name="type.dwelling"
                  onChange={this.handleChange}
                  fullWidth
                />
              </Cell>

              <Cell size="1/2">
                {isNonProfit && (
                  <SelectField
                    label={this.props.i18n('Housing type')}
                    options={[
                      {
                        value: 'PUBLIC',
                        label: this.props.i18n('Public housing')
                      },
                      {
                        value: 'YOUTH',
                        label: this.props.i18n('Youth housing')
                      },
                      {
                        value: 'FAMILY',
                        label: this.props.i18n('Family housing')
                      },
                      {
                        value: 'SENIOR',
                        label: this.props.i18n('Senior housing')
                      },
                      {
                        value: 'ELDER',
                        label: this.props.i18n('Elder housing')
                      }
                    ]}
                    nullable
                    defaultValue={data.type.tenant || 'PUBLIC' || null}
                    nullValue=""
                    name="type.tenant"
                    onChange={this.handleChange}
                    fullWidth
                  />
                )}
              </Cell>

              <Cell size="1/2" palm="1/1">
                <DatePicker
                  style={{ marginTop: -16 }}
                  label={this.props.i18n('Vacant from')}
                  // minDate={now}
                  maxDate={yearAhead}
                  inputProps={{
                    'data-cy': 'available'
                  }}
                  autoOk
                  placeholder="Snarest muligt"
                  defaultValue={data.available}
                  helperText={
                    data.available === null || data.available === undefined ? (
                      <span style={{ color: 'rgb(255, 152, 0)' }}>
                        Bemærk overtagelsesdatoen er snarest muligt
                      </span>
                    ) : null
                  }
                  name="available"
                  clearLabel={'Snarest muligt'}
                  error={errors.available}
                  onChange={this.handleChange}
                  fullWidth
                />
              </Cell>

              <Cell size="1/2" palm="1/1">
                <Grid align="center" style={{ marginBottom: 0 }}>
                  <Cell size="1/1">
                    <SelectField
                      label={this.props.i18n('Rental period')}
                      options={[
                        {
                          value: 'UNLIMITED',
                          label: this.props.i18n('Unlimited')
                        },
                        {
                          value: 'DATE',
                          label: this.props.i18n('Until a specific date')
                        },
                        {
                          value: 'RANGE',
                          label: this.props.i18n('Choose min and/or max months')
                        }
                      ]}
                      defaultValue={data.timeframe.type}
                      name="timeframe.type"
                      onChange={this.handleChange}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment icon="WatchLaterOutlined" />
                        )
                      }}
                    />
                  </Cell>
                  {data.timeframe.type === 'DATE' ? (
                    <Cell>
                      <DatePicker
                        label={this.props.i18n('Rental period end date')}
                        minDate={data.available || now}
                        autoOk
                        error={errors.timeframe && errors.timeframe.date}
                        defaultValue={data.timeframe.date}
                        name="timeframe.date"
                        inputProps={{
                          'data-cy': 'timeframe.date'
                        }}
                        onChange={this.handleChange}
                        fullWidth
                      />
                    </Cell>
                  ) : null}
                  {data.timeframe.type === 'RANGE' ? (
                    <Cell>
                      <div style={{ padding: '20px 20px 0' }}>
                        <RangeSlider
                          label={this.props.i18n(
                            'Choose min and/or max months'
                          )}
                          min={0}
                          max={49}
                          formatLabel={(value, type) => {
                            if (type === 'value') {
                              if (value === 0) {
                                return 'Min';
                              }
                              if (value === 49) {
                                return 'Maks';
                              }
                              return this.props.i18n('%s mdr', value);
                            }
                            if (type === 'min') {
                              return '';
                              // return this.props.i18n('%s mdr', (value + 1));
                            }
                            if (type === 'max') {
                              return ''; //this.props.i18n('%s mdr', (value - 1));
                            }
                          }}
                          defaultValue={{
                            min: data.timeframe.min,
                            max: data.timeframe.max
                          }}
                          name="timeframe"
                          onChange={this.handleChangeMerge}
                          fullWidth
                        />
                      </div>
                    </Cell>
                  ) : null}
                </Grid>
              </Cell>

              <Cell size="1/2" palm="1/1">
                <WithComment
                  label={this.props.i18n('Pets allowed?')}
                  name="terms[]"
                  formKey="PETS"
                  defaultValue={data.terms}
                  options={[
                    { value: '_TRUE', label: this.props.i18n('Yes') },
                    { value: '_FALSE', label: this.props.i18n('No') },
                    {
                      value: '_NULL',
                      label: this.props.i18n('Ask landlord')
                    }
                  ]}
                  // userDefinedLabel={true || false}
                  showCommentLogic={({ value, comment }) =>
                    (value !== false && value !== undefined) || comment
                  }
                  commentHint={this.props.i18n('eg. Only 1 small pet')}
                  onChange={this.handleChangeMerge}
                  startAdornment={<InputAdornment icon="PetsOutlined" />}
                />
              </Cell>

              <Cell size="1/1">
                <Grid align="center" style={{ marginBottom: 0 }}>
                  <Cell size="1/3" palm="1/1" align="top">
                    <div
                      key={'SHAREABLE'}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <WithComment
                        key={'SHAREABLE'}
                        label={this.props.i18n('Share friendly')}
                        formKey={'SHAREABLE'}
                        name="shareable"
                        defaultValue={data.shareable}
                        onChange={(ev) =>
                          this.setValue('shareable', ev.target.value?.value)
                        }
                      />
                      {
                        <ToolTipIcon
                          icon="Help"
                          text={
                            'Lejemålet egner sig som delebolig mellem to, der ikke danner par. Der er minimum to ikke sammenhængende rum, der kan bruges som to separate soveværelser.'
                          }
                        />
                      }
                    </div>
                    {/* <Checkbox
                      label={this.props.i18n('Share friendly')}
                      defaultChecked={data.shareable}
                      name="shareable"
                      onClick={this.handleChange}
                    /> */}
                    {/* <Checkbox label={ this.props.i18n('Handicap friendly? (!!)') } defaultChecked={data.handicap} name='handicap' onClick={this.handleChange} /> */}

                    {[
                      // {
                      //   label: this.props.i18n('Share friendly'),
                      //   key: 'SHAREABLE',
                      //   guide: true,
                      //   helperText:
                      //     'Lejemålet egner sig som delebolig mellem to, der ikke danner par. Der er minimum to ikke sammenhængende rum, der kan bruges som to separate soveværelser.'
                      // },
                      {
                        label: this.props.i18n('Furnised'),
                        key: 'FURNISHED',
                        guide: true,
                        helperText:
                          'Lejemålet udlejes med indbo f.eks. seng, sofa, skabe, bord og stole'
                      }
                    ]
                      .filter(Boolean)
                      .map(({ label, key, guide, helperText }) => {
                        return (
                          <div
                            key={key}
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <WithComment
                              key={key}
                              label={label}
                              formKey={key}
                              name="terms[]"
                              defaultValue={data.terms}
                              onChange={this.handleChangeMerge}
                            />
                            {guide && (
                              <ToolTipIcon icon="Help" text={helperText} />
                            )}
                          </div>
                        );
                      })}
                  </Cell>
                  <Cell size="1/3" palm="1/1" align="top">
                    {[
                      {
                        label: this.props.i18n('Sublease'),
                        key: 'SUBLEASE',
                        guide: true,
                        helperText: (
                          <>
                            Fremeleje er hvis du enten:
                            <ul>
                              <li>
                                lejer dele af en bolig ud, som du selv lejer,
                                mens du selv bor i boligen
                              </li>
                              <li>
                                lejer en hel bolig ud, som du selv lejer, mens
                                du midlertidigt ikke selv bor der
                              </li>
                            </ul>
                          </>
                        )
                      },
                      {
                        label: this.props.i18n('Commuters apartment'),
                        key: 'COMMUTERS_APARTMENT',
                        icon: 'commute',
                        guide: true,
                        helperText:
                          'Lejemålet udlejes til pendlere, der må benytte lejemålet lejlighedsvis - f.eks. mandag til fredag.'
                      }
                    ]
                      .filter(Boolean)
                      .map(({ label, key, guide, helperText }) => {
                        return (
                          <div
                            key={key}
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <WithComment
                              key={key}
                              label={label}
                              formKey={key}
                              name="terms[]"
                              defaultValue={data.terms}
                              onChange={this.handleChangeMerge}
                            />
                            {guide && (
                              <ToolTipIcon icon="Help" text={helperText} />
                            )}
                          </div>
                        );
                      })}
                  </Cell>
                  <Cell size="1/3" palm="1/1" align="top">
                    {[
                      (this._isUserBusiness() || this._isAdmin()) && {
                        label: this.props.i18n('Social housing'),
                        key: 'NONPROFIT',
                        icon: 'account_balance',
                        guide: true,
                        helperText:
                          'Lejemålet er opført med offentlig støtte og administreres og udlejes af en almen boligforening.'
                      },
                      {
                        label: this.props.i18n('Residence duty'),
                        key: 'RESIDENCEDUTY',
                        guide: true,
                        helperText:
                          'Den kommende lejer skal være tilmeldt folkeregistret på adressen.'
                      }
                    ]
                      .filter(Boolean)
                      .map(({ label, key, guide, helperText }) => {
                        return (
                          <div
                            key={key}
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <WithComment
                              key={key}
                              label={label}
                              formKey={key}
                              name="terms[]"
                              defaultValue={data.terms}
                              onChange={this.handleChangeMerge}
                            />
                            {guide && (
                              <ToolTipIcon icon="Help" text={helperText} />
                            )}
                          </div>
                        );
                      })}
                  </Cell>
                </Grid>
              </Cell>
            </Grid>

            <span style={{ fontSize: '0.8em', color: '#555' }}>
              * Påkrævet og skal udfyldes
            </span>
          </div>
        );
      }
    },
    {
      label: 'Economy',
      validate: () => {
        const { data } = this.state;
        const { economy = {}, terms } = data;
        const isNonProfit = terms.find(
          (term) => term.key === 'NONPROFIT' && term.value === true
        )
          ? true
          : false;

        const missingField = 'Du mangler at udfylde feltet';
        if (!economy.rent) {
          this.setError('economy.rent', missingField);
        }
        if (isNonProfit) {
          if (typeof economy.downpayment !== 'number') {
            this.setError('economy.downpayment', missingField);
          }
        } else {
          if (!data.fromFacebook) {
            if (typeof economy.prepaid !== 'number') {
              this.setError('economy.prepaid', missingField);
            }
            if (typeof economy.deposit !== 'number') {
              this.setError('economy.deposit', missingField);
            }
          }
        }
      },
      isComplete: () => {
        const {
          data: { economy = {} }
        } = this.state;
        return (
          Boolean(economy.rent) &&
          ((typeof economy.prepaid === 'number' &&
            typeof economy.deposit === 'number') ||
            typeof economy.downpayment === 'number')
        );
      },
      content: () => {
        const { data, errors } = this.state;
        const { economy = {} } = data;

        const isNonProfit = this.state.data.terms.find(
          (term) => term.key === 'NONPROFIT' && term.value === true
        )
          ? true
          : false;

        return this.renderWithPaper(
          <div>
            <Grid>
              <Cell size="1/3" palm="1/1">
                <Input
                  type="currency"
                  label={this.props.i18n('Rent')}
                  // placeholder={this.props.i18n('DKK per month')}
                  // startAdornment={'kr.'}
                  endAdornment={this.props.i18n('DKK per month')}
                  defaultValue={economy.rent}
                  name="economy.rent"
                  pattern="[0-9]+"
                  error={errors.economy && errors.economy.rent}
                  required
                  onChange={this.handleChange}
                  fullWidth
                  removeMargin
                />
                {economy.rent ? (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      color="primary"
                      checked={economy.inclusive}
                      name="economy.inclusive"
                      onChange={this.handleChange}
                    />
                    <Typography>
                      {this.props.i18n('Inklusiv forbrug')}
                    </Typography>
                  </div>
                ) : null}
              </Cell>
              {isNonProfit ? (
                <>
                  <Cell size="1/3" palm="1/1">
                    <Input
                      type="currency"
                      label={this.props.i18n('Down payment')}
                      endAdornment={this.props.i18n('DKK')}
                      // placeholder={this.props.i18n('DKK')}
                      defaultValue={economy.downpayment}
                      name="economy.downpayment"
                      pattern="[0-9]+"
                      error={errors.economy && errors.economy.downpayment}
                      required
                      onChange={this.handleChange}
                      fullWidth
                    />
                  </Cell>
                </>
              ) : (
                <>
                  <Cell size="1/3" palm="1/1">
                    <InputRentCalc
                      label={this.props.i18n('Prepaid rent')}
                      endAdornment={this.props.i18n('DKK')}
                      defaultValue={economy.prepaid}
                      rentValue={economy.rent}
                      name="economy.prepaid"
                      pattern="[0-9]+"
                      data-cy="economy.prepaid"
                      error={errors.economy && errors.economy.prepaid}
                      required
                      onChange={this.handleChange}
                      fullWidth
                    />
                  </Cell>
                  <Cell size="1/3" palm="1/1">
                    <InputRentCalc
                      label={this.props.i18n('Deposit')}
                      endAdornment={this.props.i18n('DKK')}
                      defaultValue={economy.deposit}
                      rentValue={economy.rent}
                      name="economy.deposit"
                      data-cy="economy.deposit"
                      pattern="[0-9]+"
                      error={errors.economy && errors.economy.deposit}
                      required
                      onChange={this.handleChange}
                      fullWidth
                    />
                  </Cell>
                </>
              )}
            </Grid>

            <Typography variant="h6" component="h3" gutterBottom>
              {this.props.i18n('Utility costs etc.')}
            </Typography>

            <Grid>
              <Cell size="1/3" palm="1/1">
                <WithComment
                  label={this.props.i18n('Water')}
                  formKey="WATER"
                  type="currency"
                  pattern="[0-9]+"
                  endAdornment={this.props.i18n('DKK per month')}
                  // placeholder={this.props.i18n('DKK per month')}
                  errors={errors}
                  name="economy.utilities[]"
                  defaultValue={economy.utilities}
                  onChange={this.handleChangeMerge}
                  fullWidth
                  disabled={economy.inclusive}
                />
              </Cell>

              <Cell size="1/3" palm="1/1">
                <WithComment
                  label={this.props.i18n('Heating')}
                  formKey="HEAT"
                  type="currency"
                  pattern="[0-9]+"
                  endAdornment={this.props.i18n('DKK per month')}
                  // placeholder={this.props.i18n('DKK per month')}
                  errors={errors}
                  name="economy.utilities[]"
                  defaultValue={economy.utilities}
                  onChange={this.handleChangeMerge}
                  fullWidth
                  disabled={economy.inclusive}
                />
              </Cell>

              <Cell size="1/3" palm="1/1">
                <WithComment
                  label={this.props.i18n('Electricity')}
                  formKey="ELECTRICITY"
                  type="currency"
                  pattern="[0-9]+"
                  endAdornment={this.props.i18n('DKK per month')}
                  // placeholder={this.props.i18n('DKK per month')}
                  errors={errors}
                  name="economy.utilities[]"
                  defaultValue={economy.utilities}
                  onChange={this.handleChangeMerge}
                  fullWidth
                  disabled={economy.inclusive}
                />
              </Cell>

              <Cell size="1/3" palm="1/1">
                <WithComment
                  label={this.props.i18n('Internet')}
                  formKey="INTERNET"
                  type="currency"
                  pattern="[0-9]+"
                  endAdornment={this.props.i18n('DKK per month')}
                  // placeholder={this.props.i18n('DKK per month')}
                  errors={errors}
                  name="economy.utilities[]"
                  defaultValue={economy.utilities}
                  onChange={this.handleChangeMerge}
                  fullWidth
                  disabled={economy.inclusive}
                />
              </Cell>

              <Cell size="1/3" palm="1/1">
                <WithComment
                  label={this.props.i18n('TV')}
                  formKey="TV"
                  type="currency"
                  pattern="[0-9]+"
                  endAdornment={this.props.i18n('DKK per month')}
                  // placeholder={this.props.i18n('DKK per month')}
                  errors={errors}
                  name="economy.utilities[]"
                  defaultValue={economy.utilities}
                  onChange={this.handleChangeMerge}
                  fullWidth
                  disabled={economy.inclusive}
                />
              </Cell>

              <Cell size="1/3" palm="1/1">
                <WithComment
                  label={this.props.i18n('Other expenses')}
                  formKey="OTHER"
                  type="currency"
                  pattern="[0-9]+"
                  endAdornment={this.props.i18n('DKK per month')}
                  // placeholder={this.props.i18n('DKK per month')}
                  errors={errors}
                  name="economy.utilities[]"
                  defaultValue={economy.utilities}
                  onChange={this.handleChangeMerge}
                  fullWidth
                  disabled={economy.inclusive}
                />
              </Cell>

              <Cell size="1/3" palm="1/1">
                <WithComment
                  label={this.props.i18n('Expenses combined')}
                  formKey="SUM"
                  type="currency"
                  pattern="[0-9]+"
                  endAdornment={this.props.i18n('DKK per month')}
                  // placeholder={this.props.i18n('DKK per month')}
                  errors={errors}
                  name="economy.utilities[]"
                  defaultValue={economy.utilities}
                  onChange={this.handleChangeMerge}
                  fullWidth
                  disabled={economy.inclusive}
                />
              </Cell>
            </Grid>

            <span style={{ fontSize: '0.8em', color: '#555' }}>
              * Påkrævet og skal udfyldes
            </span>
          </div>
        );
      }
    },
    {
      label: 'Images',
      validate: () => {},
      isComplete: () => {
        const { files } = this.state;
        return files.size ? true : null;
      },
      content: () => {
        const { files } = this.state;

        return this.renderWithPaper(
          <>
            {' '}
            {files.length >= 5 && (
              <Alert
                type="danger"
                text="Upload op til 14 billeder. Placer de vigtigste billeder først. Du kan ændre rækkefølgen ved at trække i billederne med musen."
              />
            )}
            <InputImages
              admin={this._isAdmin()}
              // TODO: Handle meta changes (eg. image type, caption, etc.)
              onChange={this.onImageChange} // merge with existing after upload
              onLoading={(saving) => this.setState({ saving })}
              upload={this.props.upload} // redux
              files={files} // what to show
            />
            <AlertWithLink
              text={
                'Vis os din bolig! Billeder siger mere end tusinde ord og sikrer dig flere henvendelser fra lejere, som kan se sig selv i din bolig.Tag billeder af alle relevante rum og ryd gerne op inden du tager billederne. Læs her hvordan du bedst tager billeder med din telefon.'
              }
              path="/assets/billedguide.pdf"
              linkText={'Download guide'}
            />
          </>
        );
      }
    } /*, {
    label: 'Facilities',
    isComplete: () => null,
    content: () => {
      const { visibleFields } = this.state;
      const { data } = this.state;
      const { facilities = {} } = data;
      const { params, dwelling, user } = this.props;

      return this.renderWithPaper(
        <div>
          <Grid>
            <Cell size='1/2' palm='1/1'>
              <h4>{ this.props.i18n('Interior') }</h4>
              <Toggle name="facilities-interior" className='bathtub' defaultToggled={ findInArray(facilities.interior, 'bathtub') } label={ this.props.i18n('Bathtub') } onToggle={ e => this.onChange(e, 'bathtub') } />
              { visibleFields.indexOf('bathtub') !== -1 || Boolean(params.uuid && findInArray(dwelling.facilities.interior, 'bathtub')) ? <Input className='bathtub' type='text' label={ this.props.i18n('Comment') } defaultValue={ findInArray(facilities.interior, 'bathtub', 'note') } name='facilities-interior' onChange={ e => this.onChange(e, 'bathtub') } /> : null }
              <Toggle name="facilities-interior" defaultToggled={ findInArray(facilities.interior, 'dishwasher') } label={ this.props.i18n('Dishwasher') } onToggle={ e => this.onChange(e, 'dishwasher') } />
              { visibleFields.indexOf('dishwasher') !== -1 || Boolean(params.uuid && findInArray(dwelling.facilities.interior, 'dishwasher')) ? <Input type='text' label={ this.props.i18n('Comment') } defaultValue={ findInArray(facilities.interior, 'dishwasher', 'note') } name='facilities-interior' onChange={ e => this.onChange(e, 'dishwasher') } /> : null }

              <h4>{ this.props.i18n('Exterior') }</h4>
              <Toggle name="facilities-exterior" className='balcony' defaultToggled={ findInArray(facilities.exterior, 'balcony') } label={ this.props.i18n('Balcony') } onToggle={ e => this.onChange(e, 'balcony') } />
              { visibleFields.indexOf('balcony') !== -1 || Boolean(params.uuid && findInArray(dwelling.facilities.exterior, 'balcony')) ? <Input className='balcony' type='text' label={ this.props.i18n('Comment') } defaultValue={ findInArray(facilities.exterior, 'balcony', 'note') } name='facilities-exterior' onChange={ e => this.onChange(e, 'balcony') } /> : null }
              <Toggle name="facilities-exterior" defaultToggled={ findInArray(facilities.exterior, 'garage') } label={ this.props.i18n('Garage') } onToggle={ e => this.onChange(e, 'garage') } />
              { visibleFields.indexOf('garage') !== -1 || Boolean(params.uuid && findInArray(dwelling.facilities.exterior, 'garage')) ? <Input type='text' label={ this.props.i18n('Comment') } defaultValue={ findInArray(facilities.exterior, 'garage', 'note') } name='facilities-exterior' onChange={ e => this.onChange(e, 'garage') } /> : null }
            </Cell>
            <Cell size='1/2' palm='1/1'>
              <h4>{ this.props.i18n('Nearby') }</h4>
              <Toggle name="facilities-nearby" className='school' defaultToggled={ findInArray(facilities.nearby, 'school') } label={ this.props.i18n('School') } onToggle={ e => this.onChange(e, 'school') } />
              { visibleFields.indexOf('school') !== -1 || Boolean(params.uuid && findInArray(dwelling.facilities.nearby, 'school')) ? <Input type='number' className='school' pattern='[0-9]+' label={ this.props.i18n('Km') } required defaultValue={ findInArray(facilities.nearby, 'school', 'value') } name='facilities-nearby' onChange={ e => this.onChange(e, 'school') } /> : null }
              <Toggle name="facilities-nearby" defaultToggled={ findInArray(facilities.nearby, 'beach') } label={ this.props.i18n('Beach') } onToggle={ e => this.onChange(e, 'beach') } />
              { visibleFields.indexOf('beach') !== -1 || Boolean(params.uuid && findInArray(dwelling.facilities.nearby, 'beach')) ? <Input type='number' pattern='[0-9]+' label={ this.props.i18n('Km') } required defaultValue={ findInArray(facilities.nearby, 'beach', 'value') } name='facilities-nearby' onChange={ e => this.onChange(e, 'beach') } /> : null }
            </Cell>
          </Grid>
        </div>
      );
    }
  }*/,
    {
      label: 'Description',
      validate: () => {
        const { data } = this.state;

        const {
          headline,
          description,
          contact = {},
          chatable,
          emailable,
          phoneable
        } = data;

        const missingField = 'Du mangler at udfylde feltet';
        if (!description) {
          this.setError('description', missingField);
        }

        if (!data.fromFacebook) {
          if (!headline) {
            this.setError('headline', missingField);
          }
          if (!contact.name) {
            this.setError('contact.name', missingField);
          }
          if (!contact.email) {
            this.setError('contact.email', missingField);
          }
          if (!contact.phone) {
            this.setError('contact.phone', missingField);
          } else {
            if (!/^[0-9+]+$/.test(contact.phone)) {
              this.setError('contact.phone', 'Ugyldig telefon nummer');
            }
          }
        } else {
          if (!contact.link) {
            if (!(contact.name && (contact.email || contact.phone))) {
              this.setError('contact.link', missingField);
            }
          }
        }

        if (
          contact.link &&
          !/^https?:\/\//.test(contact.link) &&
          !/:\/\//.test(contact.link)
        ) {
          this.setValue('contact.link', 'http://' + contact.link);
        }
        if (contact.link && !/^https?:\/\//.test(contact.link)) {
          this.setError('contact.link', "Hjemmesiden skal indeholde 'http://'");
        }
        const atLeastOneActive =
          'Mindst én af kontakt mulighederne skal være tjekket';
        const contactForms = [chatable, emailable, phoneable].filter(Boolean);
        if (!contactForms.length) {
          this.setError('contactForm', atLeastOneActive);
        } else {
          this.removeError('contactForm');
        }
      },
      isComplete: () => {
        const { data } = this.state;

        if (data.fromFacebook) {
          return (
            Boolean(data.description && data.contact.link) ||
            Boolean(
              data.description &&
                data.contact.name &&
                (data.contact.email || data.contact.phone)
            )
          );
        }
        return Boolean(
          data.headline &&
            data.description &&
            data.contact.name &&
            (data.contact.email || data.contact.phone)
        );
      },
      content: () => {
        const { data, errors, facebookAccess } = this.state;
        const maxLength = 50;
        const phoneAnonymous =
          data.anonymous && data.anonymous.includes('PHONE');
        return this.renderWithPaper(
          <div>
            <Input
              type="text"
              fullWidth
              defaultValue={data.headline}
              label={this.props.i18n('Headline')}
              name="headline"
              onChange={this.handleChange}
              inputProps={{ maxLength }}
              error={errors.headline}
              required
              withState={(props, state) => {
                const error = errors.headline;
                const length = state.length;
                const reachMax = maxLength === length;
                const helperText = (
                  <>
                    <span>{error ? error : <>&nbsp;</>}</span>{' '}
                    <span>
                      Maks {length}/{maxLength} tegn
                    </span>
                  </>
                );
                const FormHelperTextProps = {
                  error: reachMax || error,
                  style: { display: 'flex', justifyContent: 'space-between' }
                };
                return { ...props, helperText, FormHelperTextProps };
              }}
            />

            <Input
              fullWidth
              multiline
              rows={10}
              defaultValue={data.description}
              label={this.props.i18n('Description')}
              placeholder="En god annoncetekst indeholder en grundig beskrivelse af boligen. Beskriv eksempelvis boligens stand, indretning og beliggenhed, og fremhæv alle boligens særlige kvaliteter. Nævn også eventuelle forholdsregler. Jo flere informationer, desto bedre."
              name="description"
              onChange={this.handleChange}
              required
              error={errors.description}
              withState={(props, state) => {
                const length = state.length;
                const error = errors.description;

                // TODO: Warning state
                // TODO: Ord i stedet for tegn?

                const helperText = (
                  <>
                    <span>{error ? error : <>&nbsp;</>}</span>{' '}
                    <span>
                      {length < 100
                        ? `Beskrivelsen skal være mindst 100 tegn, men vi anbefaler mindst 500 - ${length}`
                        : length < 500
                        ? `Vi anbefaler at beskrivelsen er mindst 500 tegn - ${length}`
                        : ''}
                    </span>
                  </>
                );
                // const helperText =
                //   length < 100
                //     ? `Beskrivelsen skal være mindst 100 tegn, men vi anbefaler mindst 500 - ${length}`
                //     : length < 500
                //     ? `Vi anbefaler at beskrivelsen er mindst 500 tegn - ${length}`
                //     : '';
                const helperTextProps = {
                  error: true,
                  style: {
                    textAlign: 'right',
                    display: 'flex',
                    justifyContent: 'space-between'
                  }
                };
                const FormHelperTextProps = length < 500 ? helperTextProps : {};

                return { ...props, helperText, FormHelperTextProps };
              }}
            />

            <Typography variant="h6" component="h3" gutterBottom>
              {this.props.i18n('Contact information')}
            </Typography>

            <Grid>
              <Cell size="1/1" palm="1/1">
                <Input
                  type="text"
                  required
                  fullWidth
                  name="contact.name"
                  error={errors.contact && errors.contact.name}
                  label={this.props.i18n('Name')}
                  defaultValue={data.contact.name}
                  onChange={this.handleChange}
                  startAdornment={<InputAdornment icon="PersonOutlined" />}
                />
              </Cell>

              {/* <Cell size="1/2" palm="1/1">
              <Input
                type="text" fullWidth
                name="reference" label={ this.props.i18n('Reference number') }
                defaultValue={data.reference} onChange={this.handleChange}
              />
            </Cell> */}

              <Cell size="1/2" palm="1/1">
                <Input
                  type="text"
                  fullWidth
                  name="contact.email"
                  error={errors.contact && errors.contact.email}
                  label={this.props.i18n('Email')}
                  defaultValue={data.contact.email}
                  onChange={this.handleChange}
                  startAdornment={<InputAdornment icon="EmailOutlined" />}
                />
              </Cell>

              <Cell size="1/2" palm="1/1">
                <Input
                  type="text"
                  fullWidth
                  error={errors.contact && errors.contact.phone}
                  name="contact.phone"
                  label={this.props.i18n('Phone')}
                  // anonymousProps={{
                  //   isAnonymous: phoneAnonymous,
                  //   label: 'Skjul telefonnummer i annoncen',
                  //   onClick: () => this.handleAnonymous('PHONE')
                  // }}
                  required
                  defaultValue={data.contact.phone}
                  onChange={this.handleChange}
                  startAdornment={
                    <InputAdornment
                      icon={
                        phoneAnonymous
                          ? 'PhoneDisabledOutlined'
                          : 'PhoneEnabledOutlined'
                      }
                    />
                  }
                />
              </Cell>

              {(this._isUserBusiness() || facebookAccess) && (
                <Cell size="1/1" palm="1/1">
                  <Input
                    type="text"
                    fullWidth
                    error={errors.contact?.link}
                    name="contact.link"
                    label={this.props.i18n('Website')}
                    defaultValue={data.contact.link}
                    onChange={this.handleChange}
                    required={data.fromFacebook ? true : false}
                    startAdornment={<InputAdornment icon="LinkOutlined" />}
                    // TODO: Add some additional info, and only show to business landlords
                  />
                </Cell>
              )}
            </Grid>

            {!['PLUS', 'PREMIUM'].includes(data.listingType) ? null : (
              <div>
                <Typography variant="h6" component="h3" gutterBottom>
                  {this.props.i18n('Forms of contact')}
                </Typography>
                {errors.contactForm ? (
                  <Typography
                    variant="caption"
                    component="div"
                    gutterBottom
                    color="error"
                  >
                    {errors.contactForm}
                  </Typography>
                ) : null}

                <Grid>
                  <Cell size="1/3" palm="1/1">
                    <FormControlLabel
                      style={{ marginLeft: '-5px' }}
                      control={
                        <Checkbox
                          style={{ padding: '6px' }}
                          color="primary"
                          checked={data.chatable}
                          name="chatable"
                          onChange={this.handleChange}
                        />
                      }
                      label={'Beskedsystem'}
                    />
                  </Cell>
                  <Cell size="1/3" palm="1/1">
                    <FormControlLabel
                      style={{ marginLeft: '-5px' }}
                      control={
                        <Checkbox
                          style={{ padding: '6px' }}
                          color="primary"
                          checked={data.emailable}
                          name="emailable"
                          onChange={this.handleChange}
                        />
                      }
                      label={'Email'}
                    />
                  </Cell>
                  <Cell size="1/3" palm="1/1">
                    <FormControlLabel
                      style={{
                        marginLeft: '-5px'
                      }}
                      control={
                        <Checkbox
                          style={{ padding: '6px' }}
                          color="primary"
                          checked={data.phoneable}
                          name="phoneable"
                          onChange={this.handleChange}
                        />
                      }
                      label={'Telefon'}
                    />
                  </Cell>
                </Grid>
              </div>
            )}

            <span style={{ fontSize: '0.8em', color: '#555' }}>
              * Påkrævet og skal udfyldes
            </span>
          </div>
        );
      }
    },
    {
      label: 'Preview',
      validate: () => {
        const { consents } = this.state.data;
        const consentTypes = [
          'TERMS_OF_AGREEMENT',
          'TERMS_OF_SERVICE',
          'COPYRIGHT'
        ];

        let missing = 0;
        consentTypes.forEach((consentType) => {
          if (
            !consents.find(
              (consent) => consent.key === consentType && consent.value === true
            )
          ) {
            this.setError(`consents.${consentType}`, true);
            missing += 1;
          }
        });
        if (missing > 0) {
          return false;
        }
        return true;
      },
      isComplete: () => {
        const { consents } = this.state.data;
        const consentTypes = [
          'TERMS_OF_AGREEMENT',
          'TERMS_OF_SERVICE',
          'COPYRIGHT'
        ];
        let consentCounter = 0;

        for (let index = 0; index < consents.length; index++) {
          if (consents[index].value !== true) {
            return false;
          }
          switch (true) {
            case consents[index].key === 'COPYRIGHT':
              consentCounter++;
              break;
            case consents[index].key === 'TERMS_OF_AGREEMENT':
              consentCounter++;
              break;
            case consents[index].key === 'TERMS_OF_SERVICE':
              consentCounter++;
              break;
            default:
              break;
          }
          if (consentCounter === consentTypes.length) {
            return this.state.stepIndexMax === this.steps.length || null;
          }
        }
        return false;
      },
      content: () => {
        return (
          <StepPreview
            removeError={this.removeError}
            errors={this.state.errors}
            setValue={this.setValue}
            handleChangeMerge={this.handleChangeMerge}
            dwelling={fromJS(this.state.data)}
            user={this.props.user}
          />
        );
      }
    }
  ];

  renderWithPaper(children) {
    return this.state.matchMedia.palm ? (
      children
    ) : (
      <Paper
        style={{
          padding: '22px',
          marginBottom: '22px'
        }}
      >
        {children}
      </Paper>
    );
  }

  render() {
    const {
      stepIndex,
      stepIndexMax,
      feedback,
      matchMedia: { palm }
    } = this.state;

    const { params, dwelling } = this.props;
    const data = this.state.data || {};

    const hasSameDraftInState = params.uuid && data.uuid && data.uuid === params.uuid;

    if (
      params.uuid &&
      !hasSameDraftInState &&
      (!dwelling ||
        dwelling.uuid !== params.uuid ||
        !data.uuid ||
        data.uuid !== params.uuid)
    ) {
      return <div>Loading dwelling...</div>;
    }

    const stepperProps = {
      activeStep: stepIndex,
      nonLinear: true,
      orientation: palm ? 'vertical' : 'horizontal',
      style: palm ? { margin: '0 -11px' } : {}
    };

    const missingStepProps = {
      icon: <WarningIcon style={{ color: red[500] }} />,
      style: { color: red[500] }
      // optional: (
      //   <Typography variant="caption" color="error">
      //     {this.props.i18n('Missing')}
      //   </Typography>
      // )
    };
    const incompleteStepProps = {
      icon: <WarningIcon style={{ color: orange[500] }} />,
      style: { color: orange[500] }
      // optional: (
      //   <Typography variant="caption">{this.props.i18n('Optional')}</Typography>
      // )
    };

    return (
      <div className="dwelling-form">
        <Form key="dwelling-form" onSubmit={this.handleSubmit}>
          <Stepper {...stepperProps}>
            {this.steps.map((step, idx) => {
              const completed = step.isComplete
                ? step.isComplete()
                : stepIndex > idx;
              const stepButtonProps =
                stepIndexMax >= idx && stepIndex !== idx
                  ? completed === false
                    ? missingStepProps
                    : !completed
                    ? incompleteStepProps
                    : {}
                  : {};

              const stepButton =
                idx > stepIndexMax ? (
                  <StepLabel {...stepButtonProps}>
                    {this.props.i18n(step.label)}
                  </StepLabel>
                ) : (
                  <StepButton
                    onClick={() => {
                      this.setState({ feedback: null });
                      this.showStep(idx);
                    }}
                    {...stepButtonProps}
                  >
                    {this.props.i18n(step.label)}
                  </StepButton>
                );
              return stepperProps.orientation === 'vertical' ? (
                <Step key={idx} completed={completed}>
                  {stepButton}
                  <StepContent>
                    {step.content()}
                    {this.renderStepActions(idx)}
                  </StepContent>
                </Step>
              ) : (
                <Step key={idx} completed={completed}>
                  {stepButton}
                </Step>
              );
            })}
          </Stepper>
          {stepperProps.orientation !== 'vertical' && (
            <div style={{ padding: '14px 0 0', maxWidth: 800, margin: 'auto' }}>
              {this.steps[stepIndex].content()}

              {feedback ? (
                <Alert type={feedback.type} text={feedback.response} />
              ) : null}

              {this.renderStepActions(stepIndex)}
            </div>
          )}
        </Form>
      </div>
    );
  }
}

function UpdateWithRouter(props) {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  return (
    <Update
      key="dwelling-form"
      {...props}
      navigate={navigate}
      params={params}
      location={location}
    />
  );
}

UpdateWithRouter.fetchData = Update.fetchData;

export default connect(
  ({ auth: { user }, org: { dwellings } }) => ({
    dwelling: dwellings.single,
    user
  }),
  { createDwelling, upload, updateDwelling, setTitle, singleDwelling, clear }
)(i18n(UpdateWithRouter));
